'use client';

import { swrMutationFetcher } from '@lib/http/fetch';
import {
  Voucher,
  VoucherErrorCode,
  VoucherRedeemStatus,
} from '@lib/types/api/adapters/Voucher';
import { RedeemVoucherFormContent } from '@lib/types/cms/RedeemVoucherForm';
import { useCallback, useEffect, useState } from 'react';
import useSWRMutation from 'swr/mutation';

export const useRedeemVoucher = (
  errorMessages: RedeemVoucherFormContent['inputErrors']
) => {
  const {
    data: voucher,
    trigger,
    isMutating,
    error: networkError,
    reset,
  } = useSWRMutation<Voucher>(
    '/api/asv2/account/redeem-voucher',
    (url, { arg: code }: { arg: string }) =>
      swrMutationFetcher(`${url}/${encodeURIComponent(code)}`)
  );

  const [redeemError, setRedeemError] = useState<string>();

  useEffect(() => {
    if (voucher?.errorCode) {
      switch (voucher.errorCode) {
        case VoucherErrorCode.Redeemed:
          setRedeemError(
            errorMessages?.redeemedCode ??
              'The voucher code has already been redeemed.'
          );
          break;
        case VoucherErrorCode.Expired:
          setRedeemError(
            errorMessages?.expiredCode ?? 'The voucher code has expired.'
          );
          break;
        case VoucherErrorCode.Invalid:
          setRedeemError(
            errorMessages?.invalidCode ?? 'The voucher code is invalid.'
          );
          break;
      }
    }
  }, [
    errorMessages?.expiredCode,
    errorMessages?.invalidCode,
    errorMessages?.redeemedCode,
    voucher?.errorCode,
  ]);

  const onRedeemVoucher = useCallback(
    (redeemCode: string) => {
      if (!redeemCode) {
        setRedeemError(errorMessages?.empty ?? 'Please enter a voucher code.');
        return;
      }
      setRedeemError(undefined);
      trigger(redeemCode).catch((e) => {
        console.error(e);
      });
    },
    [errorMessages?.empty, trigger]
  );

  const isSuccess = voucher?.status === VoucherRedeemStatus.Success;

  return {
    voucher,
    onRedeemVoucher,
    isMutating,
    networkError,
    redeemError,
    reset,
    isSuccess,
  };
};
